import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Baby, User } from 'lucide-react';
import { ImageUploader } from '@/components/ImageUploader';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface OnboardingStep3Props {
  children: string[];
  childrenPhotos: { [index: number]: string };
  onUpdate: (children: string[]) => void;
  onPhotoUpdate: (index: number, photo: string | undefined) => void;
}

export const OnboardingStep3 = ({ children, childrenPhotos, onUpdate, onPhotoUpdate }: OnboardingStep3Props) => {
  const [numberOfChildren, setNumberOfChildren] = useState<number | null>(
    children.length > 0 ? children.length : null
  );
  const [localChildren, setLocalChildren] = useState<string[]>(children);

  const handleNumberChange = (num: number) => {
    setNumberOfChildren(num);
    const newChildren = Array(num).fill('').map((_, i) => localChildren[i] || '');
    setLocalChildren(newChildren);
    onUpdate(newChildren);
  };

  const handleChildNameChange = (index: number, name: string) => {
    const updatedChildren = [...localChildren];
    updatedChildren[index] = name;
    setLocalChildren(updatedChildren);
    onUpdate(updatedChildren);
  };

  if (numberOfChildren === null) {
    return (
      <div className="h-full flex flex-col justify-center px-6">
        <div className="max-w-sm mx-auto w-full space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <Baby className="h-16 w-16 mx-auto text-yellow-400" />
            <h2 className="text-3xl font-bold text-foreground">Vos enfants</h2>
            <p className="text-lg text-muted-foreground">
              Combien d'enfants avez-vous ?
            </p>
          </div>

          {/* Boutons pour le nombre d'enfants */}
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2, 3, 4, 5].map((num) => (
                <Button
                  key={num}
                  onClick={() => handleNumberChange(num)}
                  variant="outline"
                  className="h-14 text-lg font-medium"
                >
                  {num === 0 ? "Aucun" : num}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (numberOfChildren === 0) {
    return (
      <div className="h-full flex flex-col justify-center px-6">
        <div className="max-w-sm mx-auto w-full space-y-8 text-center">
          <Baby className="h-16 w-16 mx-auto text-yellow-400" />
          <div>
            <h2 className="text-3xl font-bold text-foreground mb-2">Parfait !</h2>
            <p className="text-lg text-muted-foreground">
              Vous n'avez pas d'enfants pour le moment.
            </p>
          </div>
          <Button
            onClick={() => setNumberOfChildren(null)}
            variant="outline"
            className="mx-auto"
          >
            Modifier
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-6">
      <div className="max-w-sm mx-auto space-y-8 py-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <Baby className="h-16 w-16 mx-auto text-yellow-400" />
          <h2 className="text-3xl font-bold text-foreground">Vos enfants</h2>
          <p className="text-lg text-muted-foreground">
            Ajoutez les prénoms de vos {numberOfChildren} enfant{numberOfChildren > 1 ? 's' : ''}
          </p>
          <Button
            onClick={() => setNumberOfChildren(null)}
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
          >
            Changer le nombre
          </Button>
        </div>

        {/* Liste des enfants avec design amélioré */}
        <div className="space-y-6">
          {Array.from({ length: numberOfChildren }, (_, index) => (
            <div key={index} className="flex flex-col items-center space-y-4">
              {/* Séparateur entre les enfants (sauf pour le premier) */}
              {index > 0 && (
                <div className="w-full border-t border-border/30 my-4"></div>
              )}
              
              {/* Photo ronde avec icône de profil à l'intérieur */}
              <div className="relative">
                <Avatar className="h-24 w-24 border-2 border-border">
                  <AvatarImage src={childrenPhotos[index]} alt={`Photo de ${localChildren[index] || 'l\'enfant'}`} />
                  <AvatarFallback className="bg-muted">
                    <User className="h-8 w-8 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                
                {/* Overlay avec ImageUploader pour changer la photo */}
                <div className="absolute inset-0">
                  <ImageUploader
                    currentImageUrl={childrenPhotos[index]}
                    onImageUploaded={(photo) => onPhotoUpdate(index, photo)}
                    bucketPath="person-photos"
                    size="lg"
                  >
                    <div className="h-24 w-24 rounded-full"></div>
                  </ImageUploader>
                </div>
              </div>
              
              {/* Input prénom centré */}
              <div className="w-full max-w-xs">
                <input
                  value={localChildren[index] || ''}
                  onChange={(e) => handleChildNameChange(index, e.target.value)}
                  placeholder={`Prénom de l'enfant ${index + 1}`}
                  className="w-full h-12 px-4 text-lg text-center bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Espacement pour les boutons */}
        <div className="h-20" />
      </div>
    </div>
  );
};